/**
 * Shared PDF helper for Coke @9.
 *
 * FIXES two long-standing defects on every PDF export:
 *   (a) The Rupee sign (U+20B9) was rendering as a superscript-1 because the built-in
 *       Helvetica/Times fonts have no glyph for it. We now embed Noto Sans (which does
 *       have ₹) and register it as the default font before any autoTable() runs.
 *   (b) Pagination + footer overlap. autoTable is told to reserve a bottom margin,
 *       repeat the header on each new page, never split a row, and let the totals block
 *       flow after the table. We then draw a consistent bottom-pinned footer with a
 *       Page X of Y indicator on every page (didDrawPage + total-pages substitution).
 *
 * Usage:
 *   const doc = await createPdfDoc();
 *   autoTable(doc, { ...withDefaults({ head, body, startY }) });
 *   drawTotalsBlock(doc, [{label:"Total cups", value: totalCups}, ...]);
 *   finalizePdf(doc, { footer: "Coke @9 · " + new Date().toLocaleString("en-IN") });
 *   doc.save("filename.pdf");
 */

import jsPDF from "jspdf";

const FONT_URL_REGULAR = "/fonts/NotoSans-Regular.ttf";
const FONT_URL_BOLD = "/fonts/NotoSans-Bold.ttf";
const FONT_FAMILY = "NotoSans";
const PAGE_BOTTOM_MARGIN = 24;   // mm — real space reserved for the footer/page number
const PAGE_TOP_MARGIN = 14;      // mm — space reserved for the header on continuation pages

let _fontCache = null;
async function _loadFonts() {
  if (_fontCache) return _fontCache;
  const [regResp, boldResp] = await Promise.all([fetch(FONT_URL_REGULAR), fetch(FONT_URL_BOLD)]);
  if (!regResp.ok || !boldResp.ok) throw new Error("Font fetch failed");
  const [regBuf, boldBuf] = await Promise.all([regResp.arrayBuffer(), boldResp.arrayBuffer()]);
  const toB64 = (buf) => {
    const bytes = new Uint8Array(buf);
    let bin = "";
    for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin);
  };
  _fontCache = { regular: toB64(regBuf), bold: toB64(boldBuf) };
  return _fontCache;
}

/** Create a jsPDF doc with Noto Sans (regular + bold) pre-registered and set as default. */
export async function createPdfDoc(options = {}) {
  const doc = new jsPDF({ unit: "mm", format: "a4", ...options });
  try {
    const fonts = await _loadFonts();
    doc.addFileToVFS(`${FONT_FAMILY}-Regular.ttf`, fonts.regular);
    doc.addFont(`${FONT_FAMILY}-Regular.ttf`, FONT_FAMILY, "normal");
    doc.addFileToVFS(`${FONT_FAMILY}-Bold.ttf`, fonts.bold);
    doc.addFont(`${FONT_FAMILY}-Bold.ttf`, FONT_FAMILY, "bold");
    doc.setFont(FONT_FAMILY, "normal");
  } catch (e) {
    // If font fetch fails (offline dev, etc.), fall back to Helvetica but log it.
    // eslint-disable-next-line no-console
    console.warn("PDF: could not load Noto Sans, ₹ will not render", e);
  }
  return doc;
}

/** Wrap an autoTable options object with the shared defaults (font, margins, header-repeat, no row split). */
export function withDefaults(opts = {}) {
  return {
    theme: "striped",
    styles: {
      font: FONT_FAMILY,
      fontStyle: "normal",
      fontSize: 9,
      cellPadding: 2,
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: [10, 10, 10],
      textColor: [255, 255, 255],
      font: FONT_FAMILY,
      fontStyle: "bold",
    },
    bodyStyles: { font: FONT_FAMILY, fontStyle: "normal" },
    alternateRowStyles: { fillColor: [248, 248, 248] },
    margin: { top: PAGE_TOP_MARGIN, bottom: PAGE_BOTTOM_MARGIN, left: 12, right: 12 },
    // Never split a row across pages
    rowPageBreak: "avoid",
    // Repeat the header row on every continuation page
    showHead: "everyPage",
    ...opts,
  };
}

/** Flow a totals/summary block AFTER the last row of the table (no absolute positioning).
 *  If not enough space is left before the reserved bottom margin, jump to a fresh page first. */
export function drawTotalsBlock(doc, items, options = {}) {
  const startY = (doc.lastAutoTable?.finalY ?? PAGE_TOP_MARGIN) + 6;
  const pageHeight = doc.internal.pageSize.getHeight();
  const need = (items.length * 6) + 8;
  const y = (startY + need > pageHeight - PAGE_BOTTOM_MARGIN) ? (doc.addPage(), PAGE_TOP_MARGIN) : startY;

  doc.setFont(FONT_FAMILY, "bold");
  doc.setFontSize(11);
  doc.setTextColor(225, 29, 42);   // brand-red
  doc.text(options.title || "Summary", 12, y);

  doc.setTextColor(20);
  doc.setFontSize(10);
  doc.setFont(FONT_FAMILY, "normal");
  items.forEach((it, i) => {
    const lineY = y + 6 + i * 5.5;
    doc.text(String(it.label) + ":", 12, lineY);
    doc.setFont(FONT_FAMILY, "bold");
    doc.text(String(it.value), 60, lineY);
    doc.setFont(FONT_FAMILY, "normal");
  });
}

/** Draw a bottom-pinned footer + "Page X of Y" on every page.
 *  Call ONCE at the very end, right before doc.save(). */
export function finalizePdf(doc, { footer = "" } = {}) {
  const pageCount = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont(FONT_FAMILY, "normal");
    doc.setFontSize(8);
    doc.setTextColor(120);
    // Left: footer text; Right: Page X of Y — both pinned inside the reserved bottom margin
    const y = pageHeight - 10;
    if (footer) doc.text(footer, 12, y);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - 12, y, { align: "right" });
    // Thin separator line above the footer
    doc.setDrawColor(220);
    doc.setLineWidth(0.2);
    doc.line(12, y - 4, pageWidth - 12, y - 4);
  }
}

/** Format INR safely for PDF (Noto Sans HAS ₹, so we can use the real glyph). */
export function fmtINR(n) {
  const num = Number(n || 0);
  return "\u20B9 " + num.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}
export const RUPEE = "\u20B9";
